import React from "react";

import { useQuery } from "@tanstack/react-query";

import { IconInfo, IconInfoConvRestart, IconLoader, IconUpload } from "@synthesis/shared-icons";
import { Button, Modal, ScrollArea, setSnack } from "@synthesis/shared-ui";
import { cn, logger } from "@synthesis/shared-utils";

import { getModelReport, getSoftwareModelByModelId, getStatus, reconvertModel } from "../../../api";
import { RUNNER_JOB_STATE_UPDATE_EVENT, useWSNotifications, type WSNotificationMessage } from "../../../../../../app/hooks";
import {
  buildConvertReportText,
  buildInProgressConvertJobStatuses,
  CONVERT_JOB_STATUS_LABELS,
  CONVERT_JOB_TITLES,
  getConvertJobKeys,
  getConvertJobStatusByState,
  mergeConvertJobStatuses,
  MODEL_REPORT_QUERY_KEY,
  MODEL_SOFTWARE_QUERY_KEY,
  MODEL_STATUS_QUERY_KEY,
  type ConvertJobStatus,
} from "./modal-convert-report.lib";
import { parseModelIds } from "./table-view.lib";

type ModalConvertReportType = {
  title: string;
  id: string;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  openModal: () => void;
  closeModal: () => void;
};

const ModalConvertReport = React.memo((props: ModalConvertReportType) => {
  const { title, id, ...root } = props;
  return (
    <Modal.Root manual {...root}>
      <ModalConvertFile title={title} id={id} />
    </Modal.Root>
  );
});

const Elements = React.memo(({ itemId, index, array }: { itemId: string; index: number; array: string[] | undefined }) => {
  return (
    <span key={itemId}>
      {itemId}
      {index < (array?.length ?? 0) - 1 && ", "}
    </span>
  );
});

const ModalConvertFile = React.memo(({ id, title }: { title: string; id: string }) => {
  const [isReconverting, setIsReconverting] = React.useState(false);
  const [socketJobStatuses, setSocketJobStatuses] = React.useState<Record<string, ConvertJobStatus | null>>({});
  const { clientWebSocket } = useWSNotifications();

  const { data: modelStatus, error: modelStatusError } = useQuery({
    queryKey: MODEL_STATUS_QUERY_KEY(id),
    queryFn: async ({ signal }) => (await getStatus({ body: { modelIds: [id] }, signal })).data,
    enabled: Boolean(id),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const {
    data: modelData,
    isPending: isModelDataPending,
    refetch: refetchModelData,
  } = useQuery({
    queryKey: MODEL_SOFTWARE_QUERY_KEY(id),
    queryFn: async ({ signal }) => (await getSoftwareModelByModelId({ path: { modelId: id }, signal })).data,
    enabled: Boolean(id),
    retry: false,
  });

  const {
    data: dataReport,
    isPending: isReportPending,
    refetch: refetchReport,
  } = useQuery({
    queryKey: MODEL_REPORT_QUERY_KEY(id),
    queryFn: async ({ signal }) => (await getModelReport({ path: { modelId: id }, signal })).data,
    enabled: Boolean(id),
    retry: false,
  });

  const jobs = React.useMemo(() => modelStatus?.find((status) => status.modelId === id)?.jobs ?? [], [modelStatus, id]);

  const error = React.useMemo(() => mergeConvertJobStatuses(jobs, socketJobStatuses), [jobs, socketJobStatuses]);

  React.useEffect(() => {
    if (!clientWebSocket || !id) {
      return;
    }

    const handleMessage = (event: MessageEvent) => {
      if (typeof event.data !== "string") {
        return;
      }

      let message: WSNotificationMessage;

      try {
        message = JSON.parse(event.data) as WSNotificationMessage;
      } catch {
        return;
      }

      if (message.name !== RUNNER_JOB_STATE_UPDATE_EVENT) {
        return;
      }

      const state = message.params?.state;

      if (!state || !parseModelIds(message.params?.modelId).includes(id)) {
        return;
      }

      const status = getConvertJobStatusByState(state, message.params?.statusMessage);
      const jobKeys = getConvertJobKeys(message.params?.outputFormat);

      setSocketJobStatuses((prev) => {
        const next = { ...prev };

        for (const jobKey of jobKeys) {
          next[jobKey] = status;
        }

        return next;
      });
    };

    clientWebSocket.addEventListener("message", handleMessage);

    return () => {
      clientWebSocket.removeEventListener("message", handleMessage);
    };
  }, [clientWebSocket, id]);

  const shownSnackRef = React.useRef<{ id: string; title: string } | null>(null);

  const showSnackOnce = React.useCallback(
    (message: string, messageType: "info" | "error") => {
      const last = shownSnackRef.current;

      if (last?.id === id && last.title === message) {
        return;
      }

      shownSnackRef.current = { id, title: message };
      setSnack({ title: message, messageType });
    },
    [id],
  );

  React.useEffect(() => {
    if (modelStatusError) {
      showSnackOnce("Не удалось получить статус обработки модели", "error");
      return;
    }

    if (!modelStatus) {
      return;
    }

    const relevantJob = jobs.find((job) => ["GEOMETRY", "PROPERTIES"].includes(job.outputFormat ?? ""));
    if (relevantJob) {
      const state = relevantJob.state?.toLowerCase();

      if (state === "waiting" || state === "running") {
        showSnackOnce("Отчет о конвертации может быть не полным, т. к. модель еще находится в процессе обработки", "info");
      } else if (state === "failed") {
        showSnackOnce("Отчет о конвертации может быть не полным, т. к. при обработке модели возникли ошибки", "error");
      }
    } else {
      const allFailed = jobs.every((job) => ["GEOMETRY", "PROPERTIES"].includes(job.outputFormat ?? "") && job.state?.toLowerCase() === "failed");

      if (allFailed) {
        showSnackOnce("Отчет о конвертации отсутствует, не удалось обработать модель", "error");
      }
    }
  }, [modelStatus, modelStatusError, jobs, showSnackOnce]);

  const handleDownloadTxt = () => {
    if (!dataReport) return;

    const reportContent = buildConvertReportText({ title, report: dataReport, model: modelData });
    const blob = new Blob([reportContent], { type: "text/plain;charset=utf-8" });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${title}_report.txt`;

    link.click();

    URL.revokeObjectURL(link.href);
  };

  const handleReconvert = async () => {
    if (isReconverting) {
      return;
    }

    setIsReconverting(true);
    setSocketJobStatuses(buildInProgressConvertJobStatuses());

    try {
      await reconvertModel(id);
      setSnack({ title: "Реконвертация модели запущена", messageType: "info" });
      await Promise.all([refetchModelData(), refetchReport()]);
    } catch (e) {
      setSocketJobStatuses({});
      logger.error(e);
      setSnack({ title: "Не удалось запустить реконвертацию модели", messageType: "error" });
    } finally {
      setIsReconverting(false);
    }
  };

  if (isModelDataPending || isReportPending) {
    return null;
  }

  return (
    <Modal.Content className="flex h-[85vh] w-[35vw] min-w-[600px] flex-col">
      <Modal.Title>Отчёт о конвертации файла</Modal.Title>
      <Modal.Description className="bg-neutrals-99 flex min-h-0 flex-1 flex-col">
        <div className="bg-neutrals-white flex min-h-0 flex-1 flex-col rounded-xl px-6 py-8">
          <h1 className="text-lg font-bold">
            <p data-cy="modalConvertReport-p-fileSystem" className="text-type-light">
              Отчёт о конвертации файла
            </p>
            <span data-cy="modalConvertReport-span-fileSystem" className="break-words">
              {title}
            </span>
          </h1>
          <div className="mt-4 flex gap-4">
            <Button data-cy="modalConvertReport-button-fileSystem" leftIcon={<IconUpload className="size-5" />} size="sm" onClick={handleDownloadTxt}>
              Выгрузить в .txt
            </Button>
            <Button
              leftIcon={isReconverting ? <IconLoader className="size-5 animate-spin" aria-hidden /> : <IconInfoConvRestart className="size-5" />}
              size="sm"
              disabled={isReconverting}
              onClick={handleReconvert}
            >
              Повторить конвертацию
            </Button>
          </div>

          <div className="flex flex-col gap-3 pt-6">
            {Object.entries(error).map(([jobKey, job]) => {
              const isError = job.type === "error";

              return (
                <div key={jobKey} className={cn("flex gap-3 rounded-lg px-4 py-3", isError ? "bg-system-error-90" : "bg-system-info-90")}>
                  {isError ? (
                    <IconInfo className="text-system-error-70 size-5" />
                  ) : (
                    <IconLoader className="text-system-info-70 size-5 animate-spin" aria-hidden />
                  )}
                  <div className="flex flex-col gap-2">
                    <span className={isError ? "text-system-error-70" : "text-system-info-70"}>
                      {CONVERT_JOB_TITLES[jobKey] ?? jobKey} — {CONVERT_JOB_STATUS_LABELS[job.type]}
                    </span>
                    {isError && (job.textError || job.date) && (
                      <span>
                        {job.textError ? `”${job.textError}”` : ""}
                        {job.date ? ` (${job.date})` : ""}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <ScrollArea rootClassName="min-h-0 flex-1" className="h-full">
            <div>
              <div className="border-neutrals-90 flex border-b">
                <div className="paragraph-base w-1/2 py-3">Незамкнутые элементы поверхности</div>
                <div className="paragraph-base text-type-common flex w-1/2 flex-col py-3">
                  {dataReport?.unclosed?.map((itemId, index) => (
                    <Elements key={itemId} itemId={itemId} index={index} array={dataReport?.unclosed} />
                  ))}
                </div>
              </div>
              <div className="border-neutrals-90 flex border-b">
                <div className="paragraph-base w-1/2 py-3">Удаленные точки модели</div>
                <div className="paragraph-base text-type-common flex w-1/2 flex-col py-3">
                  {dataReport?.deleted?.map((itemId, index) => (
                    <Elements key={itemId} itemId={itemId} index={index} array={dataReport?.deleted} />
                  ))}
                </div>
              </div>
              <div className="border-neutrals-90 flex border-b">
                <div className="paragraph-base w-1/2 py-3">Найденные прокси элементы</div>
                <div className="paragraph-base text-type-common flex w-1/2 flex-col py-3">
                  {dataReport?.proxy?.map((itemId, index) => (
                    <Elements key={itemId} itemId={itemId} index={index} array={dataReport?.proxy} />
                  ))}
                </div>
              </div>
              <div className="border-neutrals-90 flex border-b">
                <div className="paragraph-base w-1/2 py-3">Элементы без геометрии</div>
                <div className="paragraph-base text-type-common flex w-1/2 flex-col py-3">
                  {dataReport?.withoutGeometry?.map((itemId, index) => (
                    <Elements key={itemId} itemId={itemId} index={index} array={dataReport?.withoutGeometry} />
                  ))}
                </div>
              </div>
              <div className="border-neutrals-90 flex border-b">
                <div className="paragraph-base w-1/2 py-3">Нормали</div>
                <div className="paragraph-base text-type-common flex w-1/2 flex-col py-3">
                  {dataReport?.withoutNormals?.map((itemId, index) => (
                    <Elements key={itemId} itemId={itemId} index={index} array={dataReport?.withoutNormals} />
                  ))}
                </div>
              </div>
              <div className="border-neutrals-90 flex border-b">
                <div className="paragraph-base w-1/2 py-3">Пустые поля</div>
                <div className="paragraph-base text-type-common flex w-1/2 flex-col py-3">{dataReport?.emptyFieldsCount}</div>
              </div>
              <div className="border-neutrals-90 flex border-b">
                <div className="paragraph-base w-1/2 py-3">Версия ПО</div>
                {modelData && (
                  <div className="paragraph-base text-type-common flex w-1/2 flex-col py-3">
                    {modelData?.softwareName} {modelData?.softwareVersion}
                  </div>
                )}
              </div>
            </div>
          </ScrollArea>
        </div>
      </Modal.Description>
    </Modal.Content>
  );
});
export default ModalConvertReport;
